import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Space, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface Row {
  machine_id: string
  machine_code: string
  machine_name: string
  department_id: string | null
  machine_status: string | null
  health_score: number
  health_status: string
  downtime_penalty: number
  repeat_breakdown_penalty: number
  overdue_pm_penalty: number
  open_critical_penalty: number
  open_work_orders: number
  last_pm_at: string | null
  last_breakdown_at: string | null
  maintenance_cost_90d: number
  suggested_action: string | null
}

/** Spec §13 — Machine health analytics with penalty breakdown. */
export default function MachineHealthAnalyticsPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({ plant_id: plantId })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-machine-health', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.machineHealth(filters) as unknown as Promise<Row[]>,
  })
  const rows = data ?? []
  const critical = rows.filter((r) => r.health_status === 'CRITICAL' || r.health_status === 'STOPPED').length
  const watch = rows.filter((r) => r.health_status === 'WATCH' || r.health_status === 'UNDER_OBSERVATION').length

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/machine-health', filters,
        `machine-health-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    {
      title: 'Machine', dataIndex: 'machine_code', key: 'mc', width: 140,
      render: (v: string) => <Link to="/maintenance/machine-health">{v}</Link>,
    },
    { title: 'Name', dataIndex: 'machine_name', key: 'n', ellipsis: true },
    {
      title: 'Dept', key: 'd', width: 140,
      render: (_: unknown, r: Row) => deptName(r.department_id),
    },
    {
      title: 'Status', dataIndex: 'machine_status', key: 'st', width: 130,
      render: (v: string | null) => v ? <Tag>{v}</Tag> : '—',
    },
    {
      title: 'Health score', dataIndex: 'health_score', key: 'hs', width: 120,
      render: (v: number) => (
        <Text strong style={{ color: v >= 80 ? '#52c41a' : v >= 60 ? '#faad14' : '#cf1322' }}>{v}</Text>
      ),
    },
    {
      title: 'Band', dataIndex: 'health_status', key: 'hb', width: 170,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    { title: 'Downtime pen.', dataIndex: 'downtime_penalty', key: 'dp', width: 130 },
    { title: 'Repeat pen.', dataIndex: 'repeat_breakdown_penalty', key: 'rp', width: 120 },
    { title: 'Overdue PM pen.', dataIndex: 'overdue_pm_penalty', key: 'op', width: 140 },
    { title: 'Critical pen.', dataIndex: 'open_critical_penalty', key: 'cp', width: 120 },
    { title: 'Open WOs', dataIndex: 'open_work_orders', key: 'ow', width: 100 },
    {
      title: 'Last PM', dataIndex: 'last_pm_at', key: 'lp', width: 120,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM') : '—',
    },
    {
      title: 'Last BD', dataIndex: 'last_breakdown_at', key: 'lb', width: 120,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM') : '—',
    },
    {
      title: 'Cost 90d', dataIndex: 'maintenance_cost_90d', key: 'c', width: 120,
      render: (v: number) => `₹ ${v.toFixed(2)}`,
    },
    {
      title: 'Suggested action', dataIndex: 'suggested_action', key: 'sa', ellipsis: true,
      render: (v: string | null) => v ? <Text type="secondary">{v}</Text> : '—',
    },
  ]

  return (
    <div>
      <PageHeader title="Machine Health Analytics"
        subtitle="Health score, penalty breakdown, and suggested action per machine." />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ department: true, band: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Machines" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Critical / Stopped" value={critical} valueStyle={{ color: critical > 0 ? '#cf1322' : undefined }} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Watch / Under-Obs." value={watch} valueStyle={{ color: watch > 0 ? '#faad14' : undefined }} /></Card></Col>
        <Col xs={24} md={6}>
          <Card>
            <Space direction="vertical">
              <Text type="secondary">Click any machine code to open the Machine Health page for the per-machine history drawer.</Text>
            </Space>
          </Card>
        </Col>
      </Row>
      <DataTable rowKey="machine_id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
